import { useEffect, useRef, useState } from "react";
import styled, { css, keyframes } from "styled-components";
import { Colors } from "../module/constant";
import { app1, app2_1, app2_2, app3_1, app3_2, logo } from "../assets";

interface SingleFeature {
  type: "single";
  image: string;
  title: string;
  description: string;
}

interface DualFeature {
  type: "dual";
  images: [string, string];
  title: string;
  description: string;
}

type Feature = SingleFeature | DualFeature;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const RootContainer = styled.div`
  margin: 0;
  padding: 0;
  background-color: ${Colors.BLACK};
  color: #ffffff;
  overflow: hidden;
`;

const Header = styled.header<{ isScrolled: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  padding: 16px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  z-index: 1000;
  transition: background-color 0.3s ease;
  ${(props) =>
    props.isScrolled &&
    css`
      background-color: rgba(0, 0, 0, 0.8);
      backdrop-filter: blur(8px);
    `}

  @media (max-width: 768px) {
    padding: 12px 16px;
  }
`;

const Logo = styled.img`
  height: 32px;
  object-fit: contain;
  cursor: pointer;
  @media (max-width: 768px) {
    height: 24px;
  }
`;

const HeroSection = styled.section`
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
`;

const HeroBanner = styled.div`
  width: 100%;
  height: 100%;
  background-color: ${Colors.BLACK};
  object-fit: cover;
  position: absolute;
  opacity: 0.6;
`;

const HeroContent = styled.div`
  position: relative;
  text-align: center;
  max-width: 800px;
  padding: 0 20px;

  @media (max-width: 768px) {
    padding: 0 16px;
  }
`;

const HeroTitle = styled.h1`
  font-size: 64px;
  font-weight: 700;
  animation: ${fadeInUp} 0.8s ease forwards;

  @media (max-width: 1024px) {
    font-size: 48px;
  }

  @media (max-width: 768px) {
    font-size: 36px;
    margin-bottom: 16px;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 24px;
  animation: ${fadeInUp} 0.8s ease 0.2s forwards;
  opacity: 0;
  margin-top: 24px;

  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const Section = styled.section`
  min-height: 100vh;
  display: flex;
  align-items: center;
  padding: 60px 0;
  position: relative;

  @media (max-width: 768px) {
    padding: 40px 0;
  }
`;

const FeatureContainer = styled.div<{ isReverse?: boolean }>`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  align-items: center;
  gap: 60px;
  flex-direction: ${(props) => (props.isReverse ? "row-reverse" : "row")};

  @media (max-width: 1024px) {
    padding: 0 16px;
    gap: 40px;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 32px;
  }
`;

const FeatureImage = styled.img<{ isVisible: boolean; isReverse?: boolean }>`
  width: 50%;
  border-radius: 20px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  transform: translateX(${(props) => (props.isReverse ? "100px" : "-100px")});
  opacity: 0;
  transition: all 0.8s ease;

  ${(props) =>
    props.isVisible &&
    css`
      transform: translateX(0);
      opacity: 1;
    `}

  @media (max-width: 768px) {
    width: 100%;
    transform: translateY(50px);
  }
`;

const FeatureImageContainer = styled.div<{
  isVisible: boolean;
  isReverse?: boolean;
}>`
  width: 50%;
  display: flex;
  gap: 20px;
  transform: translateX(${(props) => (props.isReverse ? "100px" : "-100px")});
  opacity: 0;
  transition: all 0.8s ease;

  ${(props) =>
    props.isVisible &&
    css`
      transform: translateX(0);
      opacity: 1;
    `}

  @media (max-width: 768px) {
    width: 100%;
    flex-direction: row; // 가로 배치 유지
    gap: 16px;
    transform: translateY(50px);
  }
`;
const DualImage = styled.img`
  flex: 1;
  width: 200px;
  object-fit: cover;
  border-radius: 20px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);

  @media (max-width: 1024px) {
    width: 200px;
  }

  @media (max-width: 768px) {
    width: 100px; // 모바일에서의 높이만 조정
  }
`;

const FeatureContent = styled.div<{ isVisible: boolean; isReverse?: boolean }>`
  flex: 1;
  transform: translateX(${(props) => (props.isReverse ? "-100px" : "100px")});
  opacity: 0;
  transition: all 0.8s ease;

  ${(props) =>
    props.isVisible &&
    css`
      transform: translateX(0);
      opacity: 1;
    `}

  @media (max-width: 768px) {
    transform: translateY(50px);
  }
`;

const FeatureTitle = styled.h2`
  font-size: 36px;
  color: ${Colors.ACCENT1};
  margin-bottom: 24px;

  @media (max-width: 1024px) {
    font-size: 32px;
    margin-bottom: 20px;
  }

  @media (max-width: 768px) {
    font-size: 28px;
    margin-bottom: 16px;
  }
`;

const FeatureDescription = styled.p`
  font-size: 18px;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const StatsStickyContainer = styled.div`
  height: 100vh;
  position: relative;
`;

const StatsSticky = styled.div`
  height: 100vh;
  position: sticky;
  top: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StatsGrid = styled.div<{ isVisible: boolean }>`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 40px;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  opacity: 0;
  transform: translateY(20px);
  transition: all 0.8s ease;

  ${(props) =>
    props.isVisible &&
    css`
      opacity: 1;
      transform: translateY(0);
    `}

  @media (max-width: 1024px) {
    gap: 32px;
    padding: 0 16px;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 24px;
  }
`;

const StatCard = styled.div`
  background-color: rgba(255, 255, 255, 0.1);
  padding: 40px;
  border-radius: 20px;
  backdrop-filter: blur(8px);
  transition: transform 0.3s ease;
  cursor: pointer;

  &:hover {
    transform: translateY(-10px);
  }

  @media (max-width: 1024px) {
    padding: 32px;
  }

  @media (max-width: 768px) {
    padding: 24px;
  }
`;

const Footer = styled.footer`
  padding: 60px 0;
  background-color: rgba(0, 0, 0, 0.3);

  @media (max-width: 768px) {
    padding: 40px 0;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;

  @media (max-width: 768px) {
    padding: 0 16px;
  }
`;

const FooterLink = styled.a`
  color: #ffffff;
  text-decoration: none;
  margin-right: 20px;
  transition: color 0.2s;

  &:hover {
    color: ${Colors.ACCENT1};
  }

  @media (max-width: 768px) {
    margin-right: 16px;
    font-size: 14px;
  }
`;

const ModalOverlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
  opacity: ${(props) => (props.isOpen ? 1 : 0)};
  visibility: ${(props) => (props.isOpen ? "visible" : "hidden")};
  transition: all 0.3s ease;
`;

const ModalContent = styled.div<{ isOpen: boolean }>`
  background-color: rgba(30, 30, 30, 0.95);
  backdrop-filter: blur(10px);
  padding: 40px;
  border-radius: 20px;
  transform: ${(props) =>
    props.isOpen ? "translateY(0)" : "translateY(20px)"};
  transition: all 0.3s ease;
  min-width: 300px;
`;

const MenuButton = styled.button`
  width: 100%;
  padding: 16px 20px;
  margin: 8px 0;
  background-color: transparent;
  border: 1px solid ${Colors.ACCENT1};
  border-radius: 10px;
  color: white;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${Colors.ACCENT1};
    transform: translateY(-2px);
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: transparent;
  border: none;
  color: white;
  font-size: 24px;
  cursor: pointer;
  padding: 8px;
`;

const WelcomePage: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [visibleSections, setVisibleSections] = useState<Set<number>>(
    new Set()
  );
  const [statsVisible, setStatsVisible] = useState(false);
  const sectionRefs = useRef<(HTMLElement | null)[]>([]);
  const statsRef = useRef<HTMLDivElement>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const handleLogoClick = () => {
    setIsMenuOpen(true);
  };

  const handleCloseMenu = () => {
    setIsMenuOpen(false);
  };

  const handleMenuItemClick = (url: string) => {
    window.location.href = url;
    setIsMenuOpen(false);
  };

  useEffect(() => {
    if (isMenuOpen) {
      // 모달이 열릴 때 스크롤 방지
      document.body.style.overflow = "hidden";
    } else {
      // 모달이 닫힐 때 스크롤 복구
      document.body.style.overflow = "unset";
    }

    // 컴포넌트 언마운트시 스크롤 복구
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isMenuOpen]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;

      setScrolled(scrollY > 50);

      sectionRefs.current.forEach((ref, index) => {
        if (ref) {
          const rect = ref.getBoundingClientRect();
          const isVisible =
            rect.top < window.innerHeight * 0.75 && rect.bottom >= 0;

          setVisibleSections((prev) => {
            const newSet = new Set(prev);
            if (isVisible) {
              newSet.add(index);
            } else {
              newSet.delete(index);
            }
            return newSet;
          });
        }
      });

      if (statsRef.current) {
        const statsRect = statsRef.current.getBoundingClientRect();
        const isStatsVisible =
          statsRect.top < window.innerHeight * 0.75 && statsRect.bottom >= 0;
        setStatsVisible(isStatsVisible);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const features: Feature[] = [
    {
      image: app1,
      title: "음성 기반 AI 기술",
      description:
        "최첨단 AI 기술을 활용하여 사용자가 원하는 목소리로 노래를 제작합니다.",
      type: "single",
    },
    {
      images: [app2_1, app2_2],
      title: "다양한 음악 콘텐츠",
      description:
        "원곡부터 커버, 라이브 버전까지 풍부한 음악 콘텐츠를 제공합니다.",
      type: "dual",
    },
    {
      images: [app3_1, app3_2],
      title: "맞춤형 음악 경험",
      description: "사용자의 취향에 맞는 음악을 추천하고 제작합니다.",
      type: "dual",
    },
  ];

  return (
    <RootContainer>
      <Header isScrolled={scrolled}>
        <div onClick={handleLogoClick}>
          <Logo src={logo} alt="Homebrew" />
        </div>
      </Header>
      {/* <HandWriteText /> */}

      <HeroSection>
        <HeroBanner />
        <HeroContent>
          <HeroTitle>좋아하는 노래를.</HeroTitle>
          <HeroTitle>좋아하는 목소리로.</HeroTitle>
          <HeroSubtitle>새로운 음악의 시대를 열어갑니다.</HeroSubtitle>
        </HeroContent>
      </HeroSection>

      {features.map((feature, index) => {
        const isReversed = index === 1;

        return (
          <Section key={index} ref={(el) => (sectionRefs.current[index] = el)}>
            <FeatureContainer isReverse={isReversed}>
              {feature.type === "single" ? (
                <FeatureImage
                  src={feature.image}
                  alt={feature.title}
                  isVisible={visibleSections.has(index)}
                  isReverse={isReversed}
                />
              ) : (
                <FeatureImageContainer
                  isVisible={visibleSections.has(index)}
                  isReverse={isReversed}
                >
                  <DualImage
                    src={feature.images[0]}
                    alt={`${feature.title} 1`}
                  />
                  <DualImage
                    src={feature.images[1]}
                    alt={`${feature.title} 2`}
                  />
                </FeatureImageContainer>
              )}
              <FeatureContent
                isVisible={visibleSections.has(index)}
                isReverse={isReversed}
              >
                <FeatureTitle>{feature.title}</FeatureTitle>
                <FeatureDescription>{feature.description}</FeatureDescription>
              </FeatureContent>
            </FeatureContainer>
          </Section>
        );
      })}

      <StatsStickyContainer>
        <StatsSticky ref={statsRef}>
          <StatsGrid isVisible={statsVisible}>
            <StatCard>
              <FeatureTitle>90,000+</FeatureTitle>
              <p>유튜브 구독자</p>
            </StatCard>
            <StatCard>
              <FeatureTitle>48시간</FeatureTitle>
              <p>신청곡 제작 소요 시간</p>
            </StatCard>
            <StatCard>
              <FeatureTitle>무제한</FeatureTitle>
              <p>음악 콘텐츠</p>
            </StatCard>
          </StatsGrid>
        </StatsSticky>
      </StatsStickyContainer>

      <Footer>
        <Container>
          <Logo src={logo} alt="Homebrew" />
          <div style={{ marginTop: "40px" }}>
            <FooterLink href="/privacyPolicy">개인정보 처리방침</FooterLink>
            <FooterLink href="https://pf.kakao.com/_ztcLG">1:1 문의</FooterLink>
            <FooterLink href="https://pf.kakao.com/_ztcLG">
              카카오톡 채널
            </FooterLink>
          </div>
        </Container>
      </Footer>
      {/* 메뉴 모달 */}
      <ModalOverlay isOpen={isMenuOpen} onClick={handleCloseMenu}>
        <ModalContent isOpen={isMenuOpen} onClick={(e) => e.stopPropagation()}>
          <MenuButton onClick={() => handleMenuItemClick("/privacyPolicy")}>
            개인정보 처리방침
          </MenuButton>
          <MenuButton
            onClick={() => handleMenuItemClick("https://pf.kakao.com/_ztcLG")}
          >
            1:1 문의
          </MenuButton>
        </ModalContent>
      </ModalOverlay>
    </RootContainer>
  );
};

export default WelcomePage;
